import { Link, Navigate, Outlet, useNavigate, useParams, useRouterState } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { post } from "../lib/api";
import { useMe } from "../lib/session";
import { Loading } from "./ui";

/** Gate for signed-in pages: redirects to login, the MFA challenge, or required security steps. */
export function AuthGate({ children, allowIncomplete }: { children: ReactNode; allowIncomplete?: boolean }) {
  const me = useMe();
  const loc = useRouterState({ select: (s) => s.location });
  if (me.isLoading) return <Loading />;
  if (!me.data) return <Navigate to="/login" search={{ next: loc.href }} />;
  const state = me.data.sessionState;
  if (state === "mfa_required" && loc.pathname !== "/mfa") return <Navigate to="/mfa" />;
  if (!allowIncomplete && (state === "password_change_required" || state === "mfa_enrolment_required")) return <Navigate to="/security" search={{ required: "1" }} />;
  return <>{children}</>;
}

function NavItem({ to, children, exact }: { to: string; children: ReactNode; exact?: boolean }) {
  const path = useRouterState({ select: (s) => s.location.pathname });
  const active = exact ? path === to : path === to || path.startsWith(`${to}/`);
  return (
    <Link
      to={to}
      className={`focus-ring block rounded-lg px-3 py-2 text-sm ${active ? "bg-accent-soft font-medium text-accent" : "hover:bg-[var(--surface-2)]"}`}
    >
      {children}
    </Link>
  );
}

export function AppShell() {
  const me = useMe();
  const params = useParams({ strict: false }) as { workspaceId?: string; partnerId?: string };
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [menuOpen, setMenuOpen] = useState(false);
  const data = me.data!;
  const ws = data.workspaces.find((w) => w.id === params.workspaceId);
  const partner = data.partners.find((p) => p.id === params.partnerId);

  const logout = async () => {
    await post("/api/auth/logout");
    qc.clear();
    navigate({ to: "/login" });
  };

  const switcherValue = params.workspaceId ? `w:${params.workspaceId}` : params.partnerId ? `p:${params.partnerId}` : "";

  return (
    <div className="min-h-screen md:flex">
      <aside className={`surface border-default z-20 w-full shrink-0 border-r md:sticky md:top-0 md:block md:h-screen md:w-64 md:border-y-0 md:border-l-0 ${menuOpen ? "block" : "hidden"}`}>
        <div className="flex h-full flex-col p-4">
          <Link to="/" className="mb-5 flex items-center gap-2 font-semibold">
            <img src="/favicon.svg" alt="" className="h-7 w-7" /> Cloud Migration Hub
          </Link>
          <label className="muted mb-1 text-xs font-medium uppercase" htmlFor="switcher">
            Workspace
          </label>
          <select
            id="switcher"
            className="focus-ring mb-4 w-full rounded-lg border border-default bg-[var(--surface)] px-2 py-2 text-sm"
            value={switcherValue}
            onChange={(e) => {
              const v = e.target.value;
              if (v === "new") navigate({ to: "/onboarding" });
              else if (v.startsWith("w:")) navigate({ to: "/w/$workspaceId", params: { workspaceId: v.slice(2) } });
              else if (v.startsWith("p:")) navigate({ to: "/p/$partnerId", params: { partnerId: v.slice(2) } });
            }}
          >
            {!switcherValue && <option value="">Choose…</option>}
            {data.partners.length > 0 && (
              <optgroup label="Partner accounts">
                {data.partners.map((p) => (
                  <option key={p.id} value={`p:${p.id}`}>
                    {p.name}
                  </option>
                ))}
              </optgroup>
            )}
            <optgroup label="Workspaces">
              {data.workspaces.map((w) => (
                <option key={w.id} value={`w:${w.id}`}>
                  {w.name}
                  {w.via === "partner" ? " (client)" : ""}
                </option>
              ))}
            </optgroup>
            <option value="new">+ New workspace or partner…</option>
          </select>

          <nav className="flex-1 space-y-1 overflow-y-auto">
            {ws && (
              <>
                <NavItem to={`/w/${ws.id}`} exact>
                  Overview
                </NavItem>
                <NavItem to={`/w/${ws.id}/migrations`}>Migrations</NavItem>
                <NavItem to={`/w/${ws.id}/connections`}>Connections</NavItem>
                <NavItem to={`/w/${ws.id}/files`}>Files</NavItem>
                <NavItem to={`/w/${ws.id}/billing`}>Credits & billing</NavItem>
                <NavItem to={`/w/${ws.id}/settings`}>Settings</NavItem>
              </>
            )}
            {partner && (
              <>
                <NavItem to={`/p/${partner.id}`} exact>
                  Clients
                </NavItem>
                <NavItem to={`/p/${partner.id}/report`}>Usage & margin</NavItem>
                <NavItem to={`/p/${partner.id}/team`}>Team</NavItem>
              </>
            )}
            {data.user.isPlatformAdmin && (
              <div className="border-default mt-4 border-t pt-4">
                <NavItem to="/admin">Platform admin</NavItem>
              </div>
            )}
          </nav>

          <div className="border-default mt-4 border-t pt-4 text-sm">
            <div className="truncate font-medium">{data.user.name}</div>
            <div className="muted truncate text-xs">{data.user.email}</div>
            <div className="mt-2 flex gap-3">
              <Link to="/security" className="text-accent text-xs hover:underline">
                Security
              </Link>
              <button onClick={logout} className="text-accent text-xs hover:underline">
                Sign out
              </button>
            </div>
          </div>
        </div>
      </aside>

      <div className="min-w-0 flex-1">
        <div className="surface border-default flex items-center justify-between border-x-0 border-t-0 px-4 py-3 md:hidden">
          <span className="font-semibold">Cloud Migration Hub</span>
          <button className="focus-ring rounded-lg border border-default px-3 py-1 text-sm" onClick={() => setMenuOpen((o) => !o)} aria-expanded={menuOpen}>
            Menu
          </button>
        </div>
        {!data.user.emailVerified && <VerifyBanner />}
        <main className="mx-auto max-w-6xl px-4 py-6 md:px-8 md:py-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function VerifyBanner() {
  const [sent, setSent] = useState(false);
  return (
    <div className="border-default bg-accent-soft border-b px-4 py-2 text-sm text-accent md:px-8">
      Verify your email address to connect systems and run migrations.{" "}
      <button
        className="font-medium underline"
        disabled={sent}
        onClick={async () => {
          await post("/api/auth/verify-email/resend");
          setSent(true);
        }}
      >
        {sent ? "Email sent" : "Resend email"}
      </button>
    </div>
  );
}
